import {
  useEffect,
  useState
} from "react";

import {
  useNavigate,
  useParams
} from "react-router-dom";

import CategoryStatusBadge from "../components/CategoryStatusBadge";

import Button from "../../../components/common/Button";
import Loader from "../../../components/common/Loader";
import ErrorMessage from "../../../components/common/ErrorMessage";

import { useCategories } from "../hooks/useCategories";

import { useAuth } from "../../auth/hooks/useAuth";
import "../styles/category-details.css";
const CategoryDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { user } = useAuth();

  const {
    fetchCategory,
    updateCategory,
    deleteCategory
  } = useCategories({
    activeOnly: false
  });

  const [category, setCategory] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [actionLoading, setActionLoading] =
    useState(false);

  const [error, setError] =
    useState("");


  const isAdmin =
    user?.roles?.includes("ADMIN");


  /*
  |--------------------------------------------------------------------------
  | LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const loadCategory = async () => {
      try {
        setLoading(true);
        setError("");

        const result =
          await fetchCategory(id);

        if (mounted) {
          setCategory(result);
        }
      } catch (error) {
        if (mounted) {
          setError(
            error?.response?.data
              ?.message ||
            "Unable to load category."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      loadCategory();
    }

    return () => {
      mounted = false;
    };
  }, [id, fetchCategory]);


  /*
  |--------------------------------------------------------------------------
  | STATUS
  |--------------------------------------------------------------------------
  */

  const handleToggleStatus =
    async () => {
      if (!category) {
        return;
      }

      const nextStatus =
        !category.is_active;

      const action =
        nextStatus
          ? "activate"
          : "deactivate";

      const confirmed =
        window.confirm(
          `Are you sure you want to ${action} "${category.name}"?`
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(true);

        const updated =
          await updateCategory(
            id,
            {
              isActive: nextStatus
            }
          );

        setCategory(updated);
      } catch (error) {
        window.alert(
          error?.response?.data
            ?.message ||
          `Unable to ${action} category.`
        );
      } finally {
        setActionLoading(false);
      }
    };


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete =
    async () => {
      if (!category) {
        return;
      }

      const confirmed =
        window.confirm(
          `Are you sure you want to permanently delete "${category.name}"?`
        );

      if (!confirmed) {
        return;
      }

      try {
        setActionLoading(true);

        await deleteCategory(id);

        navigate(
          "/categories",
          { replace: true }
        );
      } catch (error) {
        window.alert(
          error?.response?.data
            ?.message ||
          "Unable to delete category."
        );
      } finally {
        setActionLoading(false);
      }
    };


  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }


  if (error) {
    return (
      <div className="max-w-2xl">
        <ErrorMessage
          message={error}
        />

        <div className="mt-4">
          <Button
            variant="secondary"
            onClick={() =>
              navigate("/categories")
            }
          >
            Back to categories
          </Button>
        </div>
      </div>
    );
  }


  if (!category) {
    return null;
  }


  return (
    <div className="max-w-5xl space-y-6">
      {/* HEADER */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() =>
              navigate("/categories")
            }
            className="mb-3 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            ← Back to categories
          </button>

          <h1 className="text-2xl font-bold text-slate-900">
            Category details
          </h1>

          <p className="mt-1 text-sm text-slate-500">
            View and manage category information.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                `/categories/${id}/edit`
              )
            }
          >
            Edit category
          </Button>

          <Button
            variant="ghost"
            loading={actionLoading}
            disabled={actionLoading}
            onClick={handleToggleStatus}
          >
            {category.is_active
              ? "Deactivate"
              : "Activate"}
          </Button>

          {isAdmin && (
            <Button
              variant="danger"
              loading={actionLoading}
              disabled={actionLoading}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </div>
      </div>


      {/* MAIN CARD */}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 sm:px-8">
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white text-xl font-bold text-blue-700">
              {category.name
                ?.charAt(0)
                ?.toUpperCase()}
            </div>

            <div>
              <h2 className="text-xl font-bold text-white">
                {category.name}
              </h2>

              <p className="mt-1 text-sm text-blue-100">
                {category.slug}
              </p>
            </div>
          </div>
        </div>


        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <InfoItem
              label="Name"
              value={category.name}
            />

            <InfoItem
              label="Slug"
              value={category.slug}
              mono
            />

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Status
              </p>

              <div className="mt-2">
                <CategoryStatusBadge
                  isActive={
                    category.is_active
                  }
                />
              </div>
            </div>

            <InfoItem
              label="Display order"
              value={
                category.display_order
              }
            />

            <InfoItem
              label="Category ID"
              value={category.id}
              mono
            />

            <InfoItem
              label="Created at"
              value={formatDate(
                category.created_at
              )}
            />

            <InfoItem
              label="Last updated"
              value={formatDate(
                category.updated_at
              )}
            />
          </div>


          <div className="mt-8 border-t border-slate-200 pt-6">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Description
            </p>

            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {category.description ||
                "No description provided."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


const InfoItem = ({
  label,
  value,
  mono = false
}) => {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p
        className={`mt-2 text-sm text-slate-800 ${
          mono
            ? "break-all font-mono"
            : ""
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
};


const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleString(
    "en-IN",
    {
      dateStyle: "medium",
      timeStyle: "short"
    }
  );
};


export default CategoryDetailsPage;